import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, fmtHUF } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, Mail, Calendar as CalIcon, CheckCircle2, XCircle, Clock, Trash2 } from "lucide-react";

const STATUS_LABEL = { pending: "Függőben", confirmed: "Visszaigazolva", cancelled: "Lemondva" };
const STATUS_COLOR = {
  pending: "border-[#BFA588] text-[#BFA588] bg-[#FAF9F6]",
  confirmed: "border-[#3E5C46] text-[#3E5C46] bg-[#F1F5F2]",
  cancelled: "border-[#9E2A2B] text-[#9E2A2B] bg-[#FBF3F3]",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const adminEmail = localStorage.getItem("cw_admin_email") || "";

  const load = async () => {
    setLoading(true);
    try {
      const [b, c] = await Promise.all([
        api.get("/admin/bookings"),
        api.get("/admin/contacts"),
      ]);
      setBookings(b.data || []);
      setContacts(c.data || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("cw_admin_token");
        navigate("/admin/login");
      } else {
        toast.error("Adatok betöltése sikertelen");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("cw_admin_token")) {
      navigate("/admin/login");
      return;
    }
    load();
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      toast.success("Státusz frissítve");
      await load();
    } catch (err) {
      toast.error("Hiba a frissítéskor");
    }
  };

  const deleteBooking = async (id) => {
    if (!window.confirm("Biztosan törlöd ezt a foglalást?")) return;
    try {
      await api.delete(`/admin/bookings/${id}`);
      toast.success("Foglalás törölve");
      await load();
    } catch (err) {
      toast.error("Törlés sikertelen");
    }
  };

  const markRead = async (id) => {
    try {
      await api.patch(`/admin/contacts/${id}/read`);
      await load();
    } catch (err) {
      toast.error("Művelet sikertelen");
    }
  };

  const logout = () => {
    localStorage.removeItem("cw_admin_token");
    localStorage.removeItem("cw_admin_email");
    navigate("/admin/login");
  };

  const unreadCount = contacts.filter((c) => !c.read).length;
  const pendingCount = bookings.filter((b) => b.status === "pending").length;

  return (
    <main className="min-h-screen bg-[#FAF9F6]" data-testid="admin-dashboard">
      <header className="border-b border-[#E5E0D8] bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="font-serif-display text-2xl text-[#1A1A1A]">CoworkingVilla</Link>
            <span className="eyebrow text-[#BFA588]">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline text-sm text-[#595959]" data-testid="admin-email-display">{adminEmail}</span>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 text-[12px] tracking-[0.2em] uppercase text-[#1A1A1A] hover:text-[#BFA588]"
            >
              <LogOut className="w-4 h-4" /> Kijelentkezés
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-10">
        {/* Stats */}
        <div className="grid sm:grid-cols-3 gap-px bg-[#E5E0D8] border border-[#E5E0D8] mb-10">
          <div className="bg-white p-6">
            <p className="eyebrow mb-2">Összes foglalás</p>
            <p className="font-serif-display text-4xl text-[#1A1A1A]" data-testid="stat-bookings">{bookings.length}</p>
          </div>
          <div className="bg-white p-6">
            <p className="eyebrow mb-2">Függőben</p>
            <p className="font-serif-display text-4xl text-[#BFA588]" data-testid="stat-pending">{pendingCount}</p>
          </div>
          <div className="bg-white p-6">
            <p className="eyebrow mb-2">Olvasatlan üzenet</p>
            <p className="font-serif-display text-4xl text-[#1A1A1A]" data-testid="stat-unread">{unreadCount}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-px bg-[#E5E0D8] border border-[#E5E0D8] mb-6 w-fit">
          <button
            data-testid="tab-bookings"
            onClick={() => setTab("bookings")}
            className={`px-6 py-3 text-[12px] tracking-[0.2em] uppercase ${tab === "bookings" ? "bg-[#1A1A1A] text-white" : "bg-white text-[#1A1A1A]"}`}
          >
            <CalIcon className="inline w-4 h-4 mr-2" /> Foglalások ({bookings.length})
          </button>
          <button
            data-testid="tab-contacts"
            onClick={() => setTab("contacts")}
            className={`px-6 py-3 text-[12px] tracking-[0.2em] uppercase ${tab === "contacts" ? "bg-[#1A1A1A] text-white" : "bg-white text-[#1A1A1A]"}`}
          >
            <Mail className="inline w-4 h-4 mr-2" /> Üzenetek ({contacts.length})
          </button>
        </div>

        {loading && <p className="text-[#595959]">Betöltés…</p>}

        {!loading && tab === "bookings" && (
          <div className="bg-white border border-[#E5E0D8] overflow-x-auto" data-testid="bookings-table">
            {bookings.length === 0 ? (
              <p className="p-10 text-center text-[#595959]">Még nincs foglalás.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#E5E0D8] bg-[#FAF9F6]">
                    <th className="text-left p-4 eyebrow">Dátum</th>
                    <th className="text-left p-4 eyebrow">Idő</th>
                    <th className="text-left p-4 eyebrow">Típus</th>
                    <th className="text-left p-4 eyebrow">Foglaló</th>
                    <th className="text-left p-4 eyebrow">Összeg</th>
                    <th className="text-left p-4 eyebrow">Státusz</th>
                    <th className="text-right p-4 eyebrow">Műveletek</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b.id} className="border-b border-[#E5E0D8] hover:bg-[#FAF9F6]/50" data-testid={`booking-row-${b.id}`}>
                      <td className="p-4 font-medium">{b.date}</td>
                      <td className="p-4 text-[#595959]">
                        {b.start_hour !== null && b.start_hour !== undefined
                          ? `${String(b.start_hour).padStart(2, "0")}:00 – ${String(b.start_hour + (b.duration_hours || 1)).padStart(2, "0")}:00`
                          : "–"}
                      </td>
                      <td className="p-4 text-[#1A1A1A]">{b.type_label}</td>
                      <td className="p-4">
                        <div className="font-medium">{b.customer_name}</div>
                        <div className="text-[#595959] text-xs">{b.customer_email} · {b.customer_phone}</div>
                      </td>
                      <td className="p-4 font-medium">{fmtHUF(b.total_price)}</td>
                      <td className="p-4">
                        <span className={`inline-block px-2 py-1 text-[10px] tracking-[0.15em] uppercase border ${STATUS_COLOR[b.status]}`}>
                          {STATUS_LABEL[b.status]}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex justify-end gap-2">
                          {b.status !== "confirmed" && (
                            <button
                              data-testid={`confirm-${b.id}`}
                              onClick={() => updateStatus(b.id, "confirmed")}
                              className="p-2 text-[#3E5C46] hover:bg-[#F1F5F2]"
                              title="Visszaigazolás"
                            >
                              <CheckCircle2 className="w-4 h-4" />
                            </button>
                          )}
                          {b.status !== "cancelled" && (
                            <button
                              data-testid={`cancel-${b.id}`}
                              onClick={() => updateStatus(b.id, "cancelled")}
                              className="p-2 text-[#9E2A2B] hover:bg-[#FBF3F3]"
                              title="Lemondás"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          )}
                          {b.status !== "pending" && (
                            <button
                              data-testid={`pending-${b.id}`}
                              onClick={() => updateStatus(b.id, "pending")}
                              className="p-2 text-[#BFA588] hover:bg-[#FAF9F6]"
                              title="Függőben"
                            >
                              <Clock className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            data-testid={`delete-${b.id}`}
                            onClick={() => deleteBooking(b.id)}
                            className="p-2 text-[#595959] hover:text-[#9E2A2B]"
                            title="Törlés"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {!loading && tab === "contacts" && (
          <div className="space-y-4" data-testid="contacts-list">
            {contacts.length === 0 ? (
              <p className="p-10 text-center text-[#595959] bg-white border border-[#E5E0D8]">Még nincs üzenet.</p>
            ) : (
              contacts.map((c) => (
                <article
                  key={c.id}
                  data-testid={`contact-row-${c.id}`}
                  className={`bg-white border ${c.read ? "border-[#E5E0D8]" : "border-[#1A1A1A]"} p-6`}
                >
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="font-serif-display text-2xl text-[#1A1A1A]">{c.name}</h3>
                        {!c.read && <span className="eyebrow text-[#C5A059]">Új</span>}
                      </div>
                      <p className="text-sm text-[#595959] mt-1">
                        <a href={`mailto:${c.email}`} className="hover:text-[#1A1A1A]">{c.email}</a> · <a href={`tel:${c.phone}`} className="hover:text-[#1A1A1A]">{c.phone}</a>
                      </p>
                    </div>
                    <span className="text-xs text-[#BFA588]">{new Date(c.created_at).toLocaleString("hu-HU")}</span>
                  </div>
                  <div className="bg-[#FAF9F6] border-l-2 border-[#BFA588] p-4 text-[15px] leading-relaxed text-[#1A1A1A] whitespace-pre-wrap">
                    {c.message}
                  </div>
                  {!c.read && (
                    <button
                      data-testid={`mark-read-${c.id}`}
                      onClick={() => markRead(c.id)}
                      className="mt-4 inline-flex items-center gap-2 text-[12px] tracking-[0.2em] uppercase text-[#1A1A1A] hover:text-[#BFA588]"
                    >
                      <CheckCircle2 className="w-4 h-4" /> Olvasottnak jelöl
                    </button>
                  )}
                </article>
              ))
            )}
          </div>
        )}
      </div>
    </main>
  );
}
